import React, { useState, useEffect } from "react";
import { Button, MenuItem, Tab, Tabs, Select } from "@material-ui/core";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { useAPIV2 } from "../../yoello-lib/modules/API2/useAPIv2";
import APIExtensions from "../../yoello-lib/modules/API2/APIDefs";
import { HTTPMethods } from "../../yoello-lib/modules/API/API";
import FormTitle from "../../yoello-lib/modules/dynamicForms/FormComponents/FormTitle";
import { OrderInput } from "./TimeSlotOrders";
import Styles from "../../scss/orderSetting.module.scss";
import { IReduxAppState } from "../../reducers";
import { toCapitalCase } from "../../utils/toCapitalCase";
import FormAlongsideText from "../../yoello-lib/modules/dynamicForms/FormComponents/FormAlongsideText";

import useCheckPermissions from "../../hooks/useCheckPermissions";
import { PERMISSIONS } from "../../const/permissions.const";
import DisabledActionWrapper from "../../yoello-lib/modules/components/DisabledActionWrapper";

export interface IOrderLimitsProps {
  orderType: "COLLECTION" | "HOME_DELIVERY";
}

export interface ILimitsForm {
  order_type?: string;
  id?: string;

  monday: number | null;
  tuesday: number | null;
  wednesday: number | null;
  thursday: number | null;
  friday: number | null;
  saturday: number | null;
  sunday: number | null;

  monday_exceptions: boolean;
  tuesday_exceptions: boolean;
  wednesday_exceptions: boolean;
  thursday_exceptions: boolean;
  friday_exceptions: boolean;
  saturday_exceptions: boolean;
  sunday_exceptions: boolean;
}

const days = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const limitInitial = {
  monday: null,
  tuesday: null,
  wednesday: null,
  thursday: null,
  friday: null,
  saturday: null,
  sunday: null,

  monday_exceptions: false,
  tuesday_exceptions: false,
  wednesday_exceptions: false,
  thursday_exceptions: false,
  friday_exceptions: false,
  saturday_exceptions: false,
  sunday_exceptions: false,
};

export interface IOrderLimitException {
  id: string;
  iso_weekday: number;
  slot_start_time: string;
  slot_end_time: string;
  order_limit: string | number | null;
}

export interface IExceptionApi {
  order_limits_exceptions: IOrderLimitException[];
}


type IWeekDay =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tabs: {
      "& .MuiTabs-indicator": {
        height: "3px",
      },
    },
    tab: {
      borderBottom: "3px solid #e8e8e8",
      minWidth: 100,
      width: "13%",
    },
  })
);

const OrderLimits = ({ orderType }: IOrderLimitsProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const selectedVenueId = useSelector(
    (state: IReduxAppState) => state.selectedVenueId
  );

  // const [limitExceptions, setLimitExceptions] = useState<
  //   IOrderLimitException[]
  // >([]);
  const [activeTab, setActiveTab] = useState<IWeekDay>("monday");
  const [limitForm, setLimitForm] = useState<ILimitsForm>(limitInitial);

  const { hasPermission, onFailedInteraction } = useCheckPermissions(PERMISSIONS.ORDER_SETTINGS_MANAGEMENT);

  // const weekday = () => days.findIndex((day) => day === activeTab) + 1;

  const { callAPI: orderLimitCall, apiData, isLoading } = useAPIV2<
    ILimitsForm[] | ILimitsForm
  >(APIExtensions.orderLimits, {
    doNotCacheValue: true,
    yoelloAPIParams: { venue_id: selectedVenueId },
    apiVersion: "v3",
  });


  useEffect(() => {
    if (apiData && Array.isArray(apiData)) {
      const limitData = apiData.find((limit) => limit.order_type === orderType);
      if (limitData) setLimitForm(limitData);
    } else if (apiData && !Array.isArray(apiData)) setLimitForm(apiData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData]);

  const changeOrderLimit = (val: string | number | null) => {
    const form = { ...limitForm };
    form[activeTab] = val === null ? null : parseInt(`${val}`);
    setLimitForm(form);
  };


  const submit = async () => {
    if (!limitForm.id) {
      await orderLimitCall(HTTPMethods.POST, {
        body: {
          ...limitForm,
          venue_id: selectedVenueId,
          order_type: orderType,
        },
        successCall: t("snackbar.updateSuccess"),
        errorCall: t("snackbar.updateOrderLimitsFailed"),
      });
    } else {
      await orderLimitCall(HTTPMethods.PUT, {
        query: limitForm.id,
        body: limitForm,
        successCall: t("snackbar.updateSuccess"),
        errorCall: t("snackbar.updateOrderLimitsFailed"),
      });
    }

  };

  const title =
    orderType === "COLLECTION"
      ? t("forms.orders.collectionLimits")
      : t("forms.orders.deliveryLimits");
  const subtitle =
    orderType === "COLLECTION"
      ? t("forms.orders.collectionLimitsDesc")
      : t("forms.orders.deliveryLimitsDesc");


  return (
    <DisabledActionWrapper
      disabled={!hasPermission}
      onDisabledClick={onFailedInteraction}
    >
      <div className={Styles.orderLimits}>
        <FormTitle title={t(title)} subtitle={subtitle} />
        <div className={Styles.desktop}>
          <Tabs
            value={activeTab}
            onChange={(_e, val: IWeekDay) => setActiveTab(val)}
            indicatorColor='primary'
            centered
            className={classes.tabs}
          >
            {days.map((day) => {
              return <Tab className={classes.tab} label={t(`days.${day}`)} value={day} />;
            })}
          </Tabs>
        </div>
        <div className={Styles.mobile}>
          <Select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value as IWeekDay)}
          >
            {days.map((day) => {
              return <MenuItem value={day}>{t(`days.${day}`)}</MenuItem>;
            })}
          </Select>
        </div>

        <div className={Styles.row}>
          <div>
            <FormAlongsideText
              label={
                orderType === "COLLECTION"
                  ? t("forms.orders.collectionLimitsDefault", {
                    day: t(`days.${toCapitalCase(activeTab).toLowerCase()}`),
                  })
                  : t("forms.orders.deliveryLimitsDefault", {
                    day: t(`days.${toCapitalCase(activeTab).toLowerCase()}`),
                  })
              }
              secondLabel={t("forms.orders.limitsDefaultDesc")}
            />
          </div>
            
          <OrderInput disabled={!hasPermission} value={limitForm[activeTab]} onChange={changeOrderLimit} />
        </div>


        <div className={Styles.row}>
          <Button
            data-component-name={"SUBMIT_LIMITS"}
            style={{ maxWidth: "50vw" }}
            variant='contained'
            color='primary'
            onClick={submit}
            disabled={isLoading || !hasPermission}
          >
            {orderType === "COLLECTION"
              ? t("forms.orders.collectionLimitsUpdate")
              : t("forms.orders.deliveryLimitsUpdate")}
          </Button>
        </div>
      </div>

    </DisabledActionWrapper>
    
  );
};

export default OrderLimits;
